import { Injectable } from "@angular/core";
import { HelpdeskEventStatus, HelpDeskTicket } from "src/app/business/help-desk/help-desk-ticket";
import { ChatMessageDisplay, ChatMessage } from "./chat-message";
import { ChatUtility } from "./chat-utility";
import { TranslateUtilityService } from "src/app/system/utility/translation-utility.service";
import { AsyncSubject, ReplaySubject } from "rxjs";
import { MEAPIService } from "../../services/api/me-api.service";

@Injectable({
    providedIn: 'root'
})
export class ChatContextService {

    private displayMessages: ChatMessageDisplay[] = [];
    private chatTicket: HelpDeskTicket | null = null;

    // For Out of Business Hours Handling
    public userInputMessage: string = '';
    public businessHoursMessage: string = '';

    public userName: string = '';
    public displayName: string = '';
    public alias: string = '';

    private employeeId: string = '';

    constructor(
        private srvApi: MEAPIService,
        private translate: TranslateUtilityService
    ) {
    }

    public setChatTicket(ticket: HelpDeskTicket | null) {
        this.chatTicket = ticket;
    }

    public getChatTicket(): HelpDeskTicket | null {
        return this.chatTicket;
    }

    public pushDisplayMessage(message: ChatMessageDisplay): void {
        this.displayMessages.push(message);
    }

    public getDisplayMessages(): ChatMessageDisplay[] {
        return this.displayMessages;
    }

    public resetDisplaymessage(): void {
        this.displayMessages = [];
    }

    public setOOHContext(options: { userInputMessage: string, businessHoursMessage: string }) {
        this.userInputMessage = options.userInputMessage;
        this.businessHoursMessage = options.businessHoursMessage;
    }

    public getOOHContext(): { userInputMessage: string, businessHoursMessage: string } {
        return {
            userInputMessage: this.userInputMessage,
            businessHoursMessage: this.businessHoursMessage
        };
    }

    public parseChatResponse(ticket: HelpDeskTicket, history: string[]) {

        let buffer: ChatMessageDisplay[] = [];

        // Ticket was not owned by an employee
        if (history.length == 0) {
            let createMsg: ChatMessageDisplay = ChatUtility.buildCreateMessage(this, ticket.message, { dateDisplay: ticket.lastUpdated });
            let inQueue: boolean = ticket.status == 0 && ticket.queuePosition > 0;
            let queueMsg: ChatMessageDisplay = ChatUtility.buildQueueMessage(this, ticket, { dateDisplay: ticket.lastUpdated, inQueue: inQueue });
            buffer.push(createMsg, queueMsg);

            if (ticket.status == HelpdeskEventStatus.CLOSED_BY_CUSTOMER) {
                let endMessage: ChatMessageDisplay = ChatUtility.buildEndChatMessage(this, ticket, 'CHAT.YOU_HAVE_ENDED_THE_TICKET');
                buffer.push(endMessage);
            }
        }

        // Ticket was not owned by an employee
        if (history.length == 1) {
            let createMsg: ChatMessageDisplay = ChatUtility.buildCreateMessage(this, ticket.message, { dateDisplay: ticket.lastUpdated });
            let queueMsg: ChatMessageDisplay = ChatUtility.buildQueueMessage(this, ticket, { dateDisplay: ticket.lastUpdated, inQueue: false });
            let endMessage: ChatMessageDisplay = ChatUtility.buildEndChatMessage(this, ticket, 'CHAT.TICKET_END_OFFLINE');
            buffer.push(createMsg, queueMsg, endMessage);
        }

        for (let i = 0; i < history.length; i++) {
            let msgChat = ChatUtility.parseSocketMessage(history[i]);

            this.setEmployeeId(msgChat);

            this.createChatDisplayMessage(ticket, msgChat, buffer);
        };

        this.displayMessages = buffer;
    }

    private createChatDisplayMessage(ticket: HelpDeskTicket, msgChat: ChatMessage, buffer: ChatMessageDisplay[]) {
        let dateDisplay = ChatUtility.getDateDisplay(new Date(msgChat.timestamp!));

        switch (msgChat.helpDeskEvent) {
            case HelpdeskEventStatus.ACTIVE: // 1

                if (buffer.length == 0) {
                    let createMsg: ChatMessageDisplay = ChatUtility.buildCreateMessage(this, ticket.message, { dateDisplay, hasSent: true, hasRead: true, hasDelivered: true });
                    let queueMsg: ChatMessageDisplay = ChatUtility.buildQueueMessage(this, ticket, { dateDisplay, inQueue: false });
                    let joinChatMsg: ChatMessageDisplay = ChatUtility.buildJoinedChatMessage(this, ticket, msgChat, { dateDisplay });
                    buffer.push(createMsg, queueMsg, joinChatMsg);
                }
                else {
                    if (this.isSentByCustomer(msgChat)) return; // Skip if the message is sent by customer, which is an "routed" message acknowledgment
                    let routeMsg: ChatMessageDisplay = ChatUtility.buildRouteMessage(this, ticket, msgChat);
                    buffer.push(routeMsg);
                }
                break;
            case HelpdeskEventStatus.CLOSED_BY_CUSTOMER: //2
                buffer.push(ChatUtility.buildEndChatMessage(this, ticket, 'CHAT.YOU_HAVE_ENDED_THE_TICKET', { dateDisplay }));
                break;
            case HelpdeskEventStatus.CLOSED_BY_EMPLOYEE: { //3 
                let message = this.translate.instant('CHAT.EMPLOYEE_HAVE_ENDED_THE_TICKET', { senderName: msgChat.senderName });
                buffer.push(ChatUtility.buildEndChatMessage(this, ticket, message, { dateDisplay }));
                break;
            }
            case HelpdeskEventStatus.UNKNOWN: // -1
                buffer.push(ChatUtility.createChatMessageDisplay(this, msgChat));
                break;
            case HelpdeskEventStatus.QUEUE: // 0:
            default:
                break;
        }
    }

    protected isSentByCustomer(msgChat: ChatMessage) {
        return ((msgChat.senderId == this.userName) || (msgChat.senderId == this.alias));
    }

    public instant(key: string | Array<string>, interpolateParams?: Object): any {
        return this.translate.instant(key, interpolateParams);
    }

    public downloadFile(url: string, blobSubject: ReplaySubject<Blob | null>): AsyncSubject<boolean> {
        return this.srvApi.DownloadGet(url, '', blobSubject);
    }

    public setEmployeeId(msgChat: ChatMessage) {
        if (!msgChat.senderId) return;
        if (!this.isSentByCustomer(msgChat)) this.employeeId = msgChat.senderId;
    }

    public getEmployeeId(): string {
        return this.employeeId;
    }

}